/**
 * Shared fleet pricing-tier CRUD screen. Rendered by both the owner route
 * (`app/owner/fleet/tiers.tsx`) and the admin owner-twin
 * (`app/admin/owner/[id]/fleet-tiers.tsx`). The only difference is the API
 * client: owners hit the endpoints directly; admins pass a makeOwnerApi(ownerId)
 * client that scopes every call to one owner via ?ownerId=. Keeping one
 * component guarantees admin parity — every owner capability exists for admin.
 */
import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Picker from '../components/Picker';
import { useToast } from '../components/Toast';
import InlineConfirm from '../components/InlineConfirm';
import ContentContainer from '../components/ContentContainer';
import { useTheme } from '../context/ThemeContext';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';

/** Minimal API surface the screen needs; satisfied by both client.ts and makeOwnerApi. */
export interface TierApiClient {
  get<T = unknown>(url: string): Promise<T>;
  post<T = unknown>(url: string, body?: unknown): Promise<T>;
  patch<T = unknown>(url: string, body?: unknown): Promise<T>;
  delete<T = unknown>(url: string): Promise<T>;
}

interface VolumeDiscountRow {
  minWashes: number;
  discountPercentage: number;
}

interface PricingTier {
  id: string;
  tierName: string;
  description: string;
  pricingModel: string;
  discountPercentage: number;
  monthlyFee?: number;
  perWashRate?: number;
  minimumVehicles: number;
  volumeDiscountTiers?: VolumeDiscountRow[] | null;
  volumeDiscountPeriod?: string | null;
}

// Values must match the backend enum exactly (percentage_discount,
// fixed_monthly, per_wash_rate, volume_discount). The old fixed_per_wash /
// monthly_unlimited / tiered_volume values were silently rejected by the API;
// tiered_volume duplicated volume_discount and is gone.
const pricingModelOptions = [
  { label: 'Percentage Discount', value: 'percentage_discount' },
  { label: 'Per-Wash Deduction', value: 'per_wash_rate' },
  { label: 'Monthly Unlimited', value: 'fixed_monthly' },
  { label: 'Volume Discount', value: 'volume_discount' },
];

interface Props {
  client: TierApiClient;
  /** When set, the screen is rendered by an admin acting on behalf of this owner. */
  adminOwnerId?: string;
}

/** Fleet pricing tiers — create/edit/delete with all discount models. */
export default function FleetTiersScreen({ client, adminOwnerId }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [tierName, setTierName] = useState('');
  const [description, setDescription] = useState('');
  const [pricingModel, setPricingModel] = useState('percentage_discount');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [monthlyFee, setMonthlyFee] = useState('');
  const [perWashRate, setPerWashRate] = useState('');
  const [minimumVehicles, setMinimumVehicles] = useState('');
  const [volumeRows, setVolumeRows] = useState<Array<{ minWashes: string; discountPercentage: string }>>([]);
  const [volumePeriod, setVolumePeriod] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteTier, setConfirmDeleteTier] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const data = await client.get<PricingTier[]>('/api/fleet/pricing-tiers');
      setTiers(data);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchTiers(); }, [client]));

  const resetForm = () => {
    setTierName('');
    setDescription('');
    setPricingModel('percentage_discount');
    setDiscountPercentage('');
    setMonthlyFee('');
    setPerWashRate('');
    setMinimumVehicles('');
    setVolumeRows([]);
    setVolumePeriod('monthly');
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (tier: PricingTier) => {
    setTierName(tier.tierName);
    setDescription(tier.description);
    setPricingModel(tier.pricingModel);
    setDiscountPercentage((tier.discountPercentage ?? 0).toString());
    setMonthlyFee(tier.monthlyFee ? (tier.monthlyFee / 100).toString() : '');
    setPerWashRate(tier.perWashRate ? (tier.perWashRate / 100).toString() : '');
    setMinimumVehicles((tier.minimumVehicles ?? 0).toString());
    setVolumeRows((tier.volumeDiscountTiers ?? []).map(r => ({
      minWashes: String(r.minWashes),
      discountPercentage: String(r.discountPercentage),
    })));
    setVolumePeriod(tier.volumeDiscountPeriod ?? 'monthly');
    setEditingId(tier.id);
    setShowForm(true);
  };

  const addVolumeRow = () => setVolumeRows(rows => [...rows, { minWashes: '', discountPercentage: '' }]);
  const removeVolumeRow = (idx: number) => setVolumeRows(rows => rows.filter((_, i) => i !== idx));
  const updateVolumeRow = (idx: number, field: 'minWashes' | 'discountPercentage', value: string) =>
    setVolumeRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const handleSubmit = async () => {
    if (!tierName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Tier name is required' });
      return;
    }

    let parsedVolumeRows: VolumeDiscountRow[] | null = null;
    if (pricingModel === 'volume_discount') {
      if (volumeRows.length < 1) {
        toast.show({ type: 'error', title: 'Error', message: 'Add at least one volume threshold' });
        return;
      }
      const seen = new Set<number>();
      const rows: VolumeDiscountRow[] = [];
      for (const r of volumeRows) {
        const minWashes = parseInt(r.minWashes);
        const pct = parseFloat(r.discountPercentage);
        if (!Number.isFinite(minWashes) || minWashes < 0 || !Number.isInteger(minWashes)) {
          toast.show({ type: 'error', title: 'Error', message: 'Min washes must be a whole number ≥ 0' });
          return;
        }
        if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
          toast.show({ type: 'error', title: 'Error', message: 'Discount % must be between 0 and 100' });
          return;
        }
        if (seen.has(minWashes)) {
          toast.show({ type: 'error', title: 'Error', message: `Duplicate threshold: ${minWashes} washes` });
          return;
        }
        seen.add(minWashes);
        rows.push({ minWashes, discountPercentage: pct });
      }
      parsedVolumeRows = rows.sort((a, b) => a.minWashes - b.minWashes);
    }

    try {
      setSubmitting(true);
      const body: any = {
        tierName,
        description,
        pricingModel,
        discountPercentage: parseFloat(discountPercentage) || 0,
        minimumVehicles: parseInt(minimumVehicles) || 0,
      };

      if (monthlyFee) body.monthlyFee = Math.round(parseFloat(monthlyFee) * 100);
      if (perWashRate) body.perWashRate = Math.round(parseFloat(perWashRate) * 100);
      if (parsedVolumeRows) {
        body.volumeDiscountTiers = parsedVolumeRows;
        body.volumeDiscountPeriod = volumePeriod;
      }

      if (editingId) {
        await client.patch(`/api/fleet/pricing-tiers/${editingId}`, body);
        toast.show({ type: 'success', title: 'Success', message: 'Tier updated' });
      } else {
        await client.post('/api/fleet/pricing-tiers', body);
        toast.show({ type: 'success', title: 'Success', message: 'Tier created' });
      }

      resetForm();
      fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tierId: string) => {
    try {
      setDeleting(true);
      await client.delete(`/api/fleet/pricing-tiers/${tierId}`);
      toast.show({ type: 'success', title: 'Success', message: 'Tier deleted' });
      setConfirmDeleteTier(null);
      fetchTiers();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  if (loading && tiers.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      {adminOwnerId && (
        <Text style={styles.adminBanner}>Admin acting on behalf of owner {adminOwnerId}</Text>
      )}
      {/* Add/Edit Form */}
      {showForm ? (
        <Card style={styles.formCard}>
          <Text style={styles.formTitle}>{editingId ? 'Edit Tier' : 'Add New Tier'}</Text>

          <Input
            label="Tier Name *"
            value={tierName}
            onChangeText={setTierName}
            placeholder="e.g., Gold Fleet"
          />

          <Input
            label="Description"
            value={description}
            onChangeText={setDescription}
            placeholder="Brief description"
            multiline
          />

          <Picker
            label="Pricing Model *"
            value={pricingModel}
            onValueChange={setPricingModel}
            options={pricingModelOptions}
          />

          {pricingModel === 'percentage_discount' && (
            <Input
              label="Discount Percentage"
              value={discountPercentage}
              onChangeText={setDiscountPercentage}
              placeholder="e.g., 15"
              keyboardType="numeric"
            />
          )}

          {pricingModel === 'fixed_monthly' && (
            <Input
              label="Monthly Fee ($)"
              value={monthlyFee}
              onChangeText={setMonthlyFee}
              placeholder="e.g., 299.99"
              keyboardType="numeric"
            />
          )}

          {pricingModel === 'per_wash_rate' && (
            <>
              <Input
                label="Deduction Per Wash ($)"
                value={perWashRate}
                onChangeText={setPerWashRate}
                placeholder="e.g., 2.50"
                keyboardType="numeric"
              />
              <Text style={styles.volumeHelp}>
                A fixed dollar amount deducted from each wash's retail price on the invoice (a wash never goes below $0).
              </Text>
            </>
          )}

          {pricingModel === 'volume_discount' && (
            <View style={styles.volumeSection}>
              <Text style={styles.volumeLabel}>Volume Discount Thresholds</Text>
              <Text style={styles.volumeHelp}>
                Discount is based on total fleet washes within the measurement period below. Highest matching threshold wins.
                An account on this tier must auto-invoice on the same cadence so each invoice covers exactly one period.
              </Text>
              <Picker
                label="Measurement Period *"
                value={volumePeriod}
                onValueChange={setVolumePeriod}
                options={[
                  { label: 'Monthly', value: 'monthly' },
                  { label: 'Biweekly (every 2 weeks)', value: 'biweekly' },
                  { label: 'Weekly', value: 'weekly' },
                ]}
              />
              {volumeRows.map((row, idx) => (
                <View key={idx} style={styles.volumeRow}>
                  <View style={{ flex: 1 }}>
                    <Input
                      label={idx === 0 ? 'Min washes' : ''}
                      value={row.minWashes}
                      onChangeText={(v) => updateVolumeRow(idx, 'minWashes', v)}
                      placeholder="5"
                      keyboardType="numeric"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Input
                      label={idx === 0 ? 'Discount %' : ''}
                      value={row.discountPercentage}
                      onChangeText={(v) => updateVolumeRow(idx, 'discountPercentage', v)}
                      placeholder="5"
                      keyboardType="numeric"
                    />
                  </View>
                  <TouchableOpacity
                    onPress={() => removeVolumeRow(idx)}
                    style={[styles.volumeRemoveBtn, idx === 0 && styles.volumeRemoveBtnFirst]}
                    hitSlop={8}
                  >
                    <Ionicons name="close-circle" size={22} color={colors.danger} />
                  </TouchableOpacity>
                </View>
              ))}
              <Button
                title="Add Threshold"
                onPress={addVolumeRow}
                variant="outline"
                icon={<Ionicons name="add" size={18} color={colors.primary} />}
                style={{ marginTop: spacing.sm }}
              />
            </View>
          )}

          <Input
            label="Minimum Washes"
            value={minimumVehicles}
            onChangeText={setMinimumVehicles}
            placeholder="e.g., 5"
            keyboardType="numeric"
          />

          <View style={styles.formActions}>
            <Button
              title="Cancel"
              onPress={resetForm}
              variant="outline"
              style={{ flex: 1 }}
            />
            <Button
              title={editingId ? 'Update' : 'Create'}
              onPress={handleSubmit}
              loading={submitting}
              style={{ flex: 1 }}
            />
          </View>
        </Card>
      ) : (
        <Button
          title="Add Pricing Tier"
          onPress={() => setShowForm(true)}
          icon={<Ionicons name="add" size={20} color={colors.white} />}
          style={styles.addButton}
        />
      )}

      {/* Tiers List */}
      {tiers.length > 0 ? (
        tiers.map(tier => (
          <Card key={tier.id} style={styles.tierCard}>
            <View style={styles.tierHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.tierName}>{tier.tierName}</Text>
                {tier.description && <Text style={styles.tierDescription}>{tier.description}</Text>}
              </View>
              <View style={styles.tierActions}>
                <TouchableOpacity onPress={() => handleEdit(tier)} style={styles.iconButton}>
                  <Ionicons name="pencil" size={20} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setConfirmDeleteTier(tier.id)} style={styles.iconButton}>
                  <Ionicons name="trash" size={20} color={colors.danger} />
                </TouchableOpacity>
              </View>
            </View>

            <InlineConfirm
              message={`Are you sure you want to delete "${tier.tierName}"?`}
              visible={confirmDeleteTier === tier.id}
              onConfirm={() => handleDelete(tier.id)}
              onCancel={() => setConfirmDeleteTier(null)}
              confirmLabel="Delete"
              loading={deleting}
              variant="danger"
            />

            <View style={styles.tierDetails}>
              <View style={styles.tierDetail}>
                <Text style={styles.tierDetailLabel}>Model</Text>
                <Text style={styles.tierDetailValue}>
                  {pricingModelOptions.find(o => o.value === tier.pricingModel)?.label}
                </Text>
              </View>

              {tier.discountPercentage > 0 && tier.pricingModel !== 'volume_discount' && (
                <View style={styles.tierDetail}>
                  <Text style={styles.tierDetailLabel}>Discount</Text>
                  <Text style={styles.tierDetailValue}>{tier.discountPercentage}%</Text>
                </View>
              )}

              {tier.pricingModel === 'volume_discount' && tier.volumeDiscountTiers && tier.volumeDiscountTiers.length > 0 && (
                <>
                  <View style={styles.tierDetail}>
                    <Text style={styles.tierDetailLabel}>Period</Text>
                    <Text style={styles.tierDetailValue}>{tier.volumeDiscountPeriod ?? 'monthly'}</Text>
                  </View>
                  <View style={styles.tierDetail}>
                    <Text style={styles.tierDetailLabel}>Thresholds</Text>
                    <Text style={styles.tierDetailValue}>
                      {tier.volumeDiscountTiers.map(r => `${r.minWashes}→${r.discountPercentage}%`).join(', ')}
                    </Text>
                  </View>
                </>
              )}

              {tier.monthlyFee && (
                <View style={styles.tierDetail}>
                  <Text style={styles.tierDetailLabel}>Monthly Fee</Text>
                  <Text style={styles.tierDetailValue}>{fmt(tier.monthlyFee)}</Text>
                </View>
              )}

              {tier.perWashRate && (
                <View style={styles.tierDetail}>
                  <Text style={styles.tierDetailLabel}>Per-Wash Deduction</Text>
                  <Text style={styles.tierDetailValue}>-{fmt(tier.perWashRate)}</Text>
                </View>
              )}

              <View style={styles.tierDetail}>
                <Text style={styles.tierDetailLabel}>Min Washes</Text>
                <Text style={styles.tierDetailValue}>{tier.minimumVehicles}</Text>
              </View>
            </View>
          </Card>
        ))
      ) : !showForm && (
        <Card style={styles.emptyCard}>
          <Ionicons name="pricetag-outline" size={48} color={colors.textLight} />
          <Text style={styles.emptyTitle}>No pricing tiers yet</Text>
          <Text style={styles.emptyText}>Create pricing tiers to offer special rates to fleet customers</Text>
        </Card>
      )}

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },

  addButton: { marginBottom: spacing.lg },

  formCard: { marginBottom: spacing.lg },
  formTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  formActions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.md },

  tierCard: { marginBottom: spacing.md },
  tierHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: spacing.md },
  tierName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  tierDescription: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },
  tierActions: { flexDirection: 'row', gap: spacing.sm },
  iconButton: { padding: spacing.xs },

  tierDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: c.borderLight,
  },
  tierDetail: { minWidth: '45%' },
  tierDetailLabel: { fontSize: fontSize.xs, color: c.textLight, marginBottom: 2 },
  tierDetailValue: { fontSize: fontSize.md, fontWeight: fontWeight.medium, color: c.text },

  emptyCard: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: 280 },

  volumeSection: { marginTop: spacing.sm },
  volumeLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  volumeHelp: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  volumeRow: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm },
  volumeRemoveBtn: { paddingBottom: spacing.sm, paddingHorizontal: 4 },
  volumeRemoveBtnFirst: { paddingBottom: spacing.sm + 18 },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
